import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/**
 * Which trigger in a step may show its tooltip. A step can carry several
 * (the range step explains a band from the pill and from every header label,
 * the breakdown names each bar), and two open at once would cover each other,
 * so opening one takes the turn from whichever held it.
 */
export interface TooltipGroupValue {
  active: string | null;
  claim: (id: string) => void;
}

export const TooltipGroup = createContext<TooltipGroupValue | null>(null);

/**
 * A button that explains itself in a tooltip, shared by the report's steps.
 *
 * The same contract as the company dashboard's info tooltip: it opens on
 * hover, on keyboard focus, and on click or tap, and Escape dismisses it until
 * the pointer or focus leaves. Each trigger is tracked in state rather than
 * left to CSS `:hover` and `:focus`, because Safari does not focus a button
 * it taps, and because Escape has to beat a hover that is still in place.
 * While a tap holds it open, a press anywhere else closes it again, since a
 * button Safari did not focus has no blur to close it.
 *
 * Hover is taken from a real mouse only. A tap also fires emulated mouse
 * events, and Chrome on Android focuses the button it taps, so a tap leaves
 * hover or focus behind and a second tap could not close the tooltip by
 * unpinning it alone. Instead a press on an open tooltip dismisses it, and
 * whether it was open is read when the press starts, before the focus that
 * same tap brings.
 *
 * Inside a `TooltipGroup` every way of opening claims the group's turn, and
 * a trigger that has lost it stays shut until it claims it again.
 *
 * `onOpen` is told each time the tooltip opens, by whichever means, so a
 * caller can count which parts of a chart the learner asked about.
 *
 * The tooltip's pointer is placed at the middle of its button, measured,
 * because the tooltip is not always centred on it: the outermost band labels
 * and bars hang theirs from one edge, and at phone widths the range pill's
 * hangs from the whole heading.
 */
export const useTooltipTrigger = (onOpen?: () => void) => {
  const id = useId();
  const group = useContext(TooltipGroup);
  const active = group ? group.active : id;
  const claim = group?.claim;
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const tooltip = useRef<HTMLSpanElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const open = active === id && !dismissed && (hovered || focused || pinned);
  const openAtPress = useRef<boolean | null>(null);
  // Read through a ref so a caller passing a fresh closure each render does
  // not count one opening twice.
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    if (open) onOpenRef.current?.();
  }, [open]);

  // Listened for on the document so Escape also closes a tooltip opened by
  // hover alone, where the button did not receive focus.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setDismissed(true);
      setPinned(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    if (!pinned) return undefined;
    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setPinned(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [pinned]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const target = button.current?.getBoundingClientRect();
      const bubble = tooltip.current;
      if (!target || !bubble) return;
      const box = bubble.getBoundingClientRect();
      // Kept clear of the rounded corners, so the pointer always meets the
      // flat top edge.
      const inset = 16;
      const x = target.left + target.width / 2 - box.left;
      const clamped = Math.min(Math.max(x, inset), box.width - inset);
      bubble.style.setProperty("--pointer-x", `${clamped}px`);
    };
    place();
    // A window resize can move the button without resizing it, and the bubble
    // or the button can resize without the window, as copy wraps or a font
    // arrives. So both are listened for, the second where the browser has a
    // ResizeObserver (jsdom has none).
    window.addEventListener("resize", place);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
    if (observer && tooltip.current) observer.observe(tooltip.current);
    if (observer && button.current) observer.observe(button.current);
    return () => {
      window.removeEventListener("resize", place);
      observer?.disconnect();
    };
  }, [open]);

  // A tap pins its tooltip until a press elsewhere. Another trigger taking
  // the turn is that press, so the pin goes with it rather than reopening
  // this tooltip once the other closes.
  useEffect(() => {
    if (active !== id) setPinned(false);
  }, [active, id]);

  return {
    open,
    tooltipId: id,
    rootProps: {
      ref: root,
      onPointerEnter: (event: React.PointerEvent) => {
        if (event.pointerType !== "mouse") return;
        setHovered(true);
        claim?.(id);
      },
      onPointerLeave: (event: React.PointerEvent) => {
        if (event.pointerType !== "mouse") return;
        setHovered(false);
        if (!focused) setDismissed(false);
      },
    },
    tooltipRef: tooltip,
    buttonProps: {
      ref: button,
      type: "button" as const,
      "aria-describedby": id,
      onFocus: () => {
        setFocused(true);
        claim?.(id);
      },
      onBlur: () => {
        setFocused(false);
        setPinned(false);
        setDismissed(false);
      },
      onPointerDown: () => {
        openAtPress.current = open;
      },
      onClick: () => {
        // A keyboard press has no pointerdown before it.
        const wasOpen = openAtPress.current ?? open;
        openAtPress.current = null;
        if (wasOpen) {
          setDismissed(true);
          setPinned(false);
        } else {
          setDismissed(false);
          setPinned(true);
          claim?.(id);
        }
      },
    },
  };
};
